import { FormControl } from '@angular/forms';
import {
    IAssetRobot,
    IAssetType,
} from '@models';

import cloneDeep from 'lodash-es/cloneDeep';
import { BehaviorSubject } from 'rxjs';

import { validatorsFor } from './asset-validators';
import {
    generatePayloadFrom,
    getValueFor,
    newRobotAssetFor,
} from './asset-values';

export class RobotAssetSource {
    public source$ = new BehaviorSubject<IAssetRobot[]>([]);

    private _increment = 0;

    public get values() {
        return this._cache;
    }

    public get length() {
        return this.values.length;
    }

    constructor(private _cache: IAssetRobot[] = []) {
        this._emit();
    }

    public updateType(type: IAssetType) {
        this.values.forEach((asset, idx) => this.values[idx] = newRobotAssetFor(type, asset));

        this._emit();
    }

    public updateCredentialStore(credentialStoreId?: number) {
        this.values.forEach((asset, idx) => {
            this.values[idx] = newRobotAssetFor(asset.type, asset);
            this.values[idx].credentialStoreId = credentialStoreId;
        });

        this._emit();
    }

    public find(entity?: IAssetRobot) {
        if (!entity) { return entity; }

        return this.values.find(e => e.id === entity.id);
    }

    public persist(modified: IAssetRobot, type: IAssetType, value: string, readonly?: boolean) {
        const robotAsset = {
            ...generatePayloadFrom(
                modified,
                type,
                value,
                readonly,
            ) as IAssetRobot,
            robotName: modified.robotName,
            id: modified.id === -1 ? (Number.MIN_SAFE_INTEGER + ++this._increment) : modified.id,
        };

        const index = this.values.findIndex(r => r.id === modified.id);
        this.values[index] = robotAsset;

        this._emit();
    }

    public generateEntry(type: IAssetType, credentialStoreId?: number) {
        const newAsset = newRobotAssetFor(type, {}, credentialStoreId);
        this.values.unshift(newAsset);

        return this.find(newAsset)!;
    }

    public isAssetBoundToRobot(id: number) {
        return this.values.filter(v => v.robotId === id).length > 1;
    }

    public remove(entry: IAssetRobot) {
        this.values.splice(this.values.findIndex(e => e.id === entry.id), 1);
        this._emit();
    }

    public isCollectionValid(type: IAssetType, readonly?: boolean) {
        return this.values
                .reduce((prev, asset) => {
                    const validators = validatorsFor(type, asset.id < 0, readonly, true);
                    const dataModel = getValueFor(asset, readonly);
                    return prev && validators!({
                        value: dataModel,
                    } as FormControl) == null;
                }, true);
    }

    public destroy() {
        this.source$.complete();
    }

    public update() {
        this._emit();
    }

    private _emit() {
        this.source$.next(cloneDeep(this.values));
    }
}
